import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';

import { BOX_CONDITIONS, SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { BuyBarGlobalPricingInfo } from 'productTemplate/components/buyBar/styles/BuyBarStyles';
import { DARWIN_EXPERIMENT_NAMES } from 'shared/enums/darwinExperimentNames';
import { FeatureFlags } from 'featureFlags/constants';
import { getMParticleDeviceId } from 'tracking/utils/getMparticleDeviceId';
import { getMParticleSessionId } from 'tracking/utils/getMParticleSessionId';
import { getUserRemainingOfferDuration } from 'productTemplate/utils/buyBar/getUserRemainingOfferDuration';
import { ITopOfferData } from 'productTemplate/types/TopOfferData';
import { IUserProductIdOfferResponse } from 'productTemplate/types/UserProductIdOfferResponse';
import { logExperimentExposure } from 'experiments/utils';
import { MobileOfferSectionListItem } from 'productTemplate/components/buyBar/OfferSection/MobileOfferSectionListItem';
import { OfferSectionListItem } from 'productTemplate/components/buyBar/OfferSection/OfferSectionListItem';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

interface IOfferSectionProps {
  selectedSize: number;
}

export const OffersSection = ({ selectedSize }: IOfferSectionProps) => {
  const { isMobileOrTablet } = useWindowSize();
  const { getTopOfferBySize, getExistingOffer, getExistingOffers, showPlaceOfferModal, isLoading } =
    useOffersContext();
  const { productTemplate, buyBarVariants } = useStoreSelector((state) => state.pdp);
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const { productCategory, slug, id } = productTemplate;
  const { allSizes } = buyBarVariants;
  const { selectedCurrencyCode } = useCurrency();
  const [topOffer, setTopOffer] = useState<ITopOfferData>(null);
  const [userOffer, setUserOffer] = useState<IUserProductIdOfferResponse>(null);
  const isSneaker = productCategory === 'shoes';
  const hiddenInput = useRef<HTMLInputElement>(null);
  const { signal } = useAbortController()();
  const isOfferLoginTest = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_OFFERS_LOGIN_TEST_2024);

  const { lastSoldPriceCents, lowestPriceCents } =
    allSizes?.find((product) => product?.sizeOption?.value === selectedSize) || {};

  useEffect(() => {
    const selectedSizeUserOffer = getExistingOffer(selectedSize);
    setUserOffer(selectedSizeUserOffer as IUserProductIdOfferResponse);
  }, [getExistingOffer, selectedSize, id]);

  useEffect(() => {
    const selectedSizeTopOffer = getTopOfferBySize(selectedSize);
    setTopOffer(selectedSizeTopOffer);
  }, [getTopOfferBySize, selectedSize]);

  useEffect(() => {
    void getExistingOffers();
  }, []);

  const handleClick = async () => {
    let experimentGroup = '';

    try {
      if (!isAuthenticated && isOfferLoginTest) {
        const deviceId = getMParticleDeviceId();
        const sessionId = getMParticleSessionId();
        const userId = deviceId;

        const logExperimentResult = await logExperimentExposure({
          deviceId,
          experimentName: DARWIN_EXPERIMENT_NAMES.WEB_OFFERS_LOGIN_TEST,
          isAuthenticated,
          signal,
          optionalMetaData: { sessionId, userId },
        });

        experimentGroup = logExperimentResult?.group?.name || '';
      }
    } catch (error) {
      bugsnag(error, {
        severity: 'error',
        context: 'PDP: OffersSection',
        metadata: {
          experimentName: DARWIN_EXPERIMENT_NAMES.WEB_OFFERS_LOGIN_TEST,
          isAuthenticated,
        },
      });
    }

    // If mobile, focus on hidden input to force keyboard then switch focus to correct input (Webkit limitation)
    if (isMobileOrTablet) {
      hiddenInput?.current?.focus();
    }
    showPlaceOfferModal(selectedSize, null, null, null, experimentGroup);

    // If user has existing offer is an edit
    if (userOffer?.offerAmountCents) {
      sendTrackingEvent('OFFER_EDIT_TAP', {
        product_template_slug: slug,
        product_id: id,
        size: selectedSize,
        condition_category: userOffer?.shoeCondition,
        shoe_condition: userOffer?.shoeCondition,
        box_condition: userOffer?.boxCondition,
        wantId: userOffer?.id,
        offer_duration: userOffer?.offerDuration,
        offer_amount: userOffer?.offerAmountCents / 100,
        last_sale: lastSoldPriceCents?.amount / 100,
        top_offer: topOffer?.offerAmountCents?.amount / 100,
        buy_now: lowestPriceCents?.amount / 100,
      });
    } else {
      sendTrackingEvent('OFFER_TAP', {
        product_template_slug: slug,
        product_id: id,
        size: selectedSize,
        condition_category: SHOE_CONDITIONS.NEW,
        shoe_condition: SHOE_CONDITIONS.NEW,
        box_condition: BOX_CONDITIONS.GOOD_CONDITION,
        last_sale: lastSoldPriceCents?.amount / 100,
        top_offer: topOffer?.offerAmountCents?.amount / 100,
        buy_now: lowestPriceCents?.amount / 100,
        currency_code: selectedCurrencyCode,
        event_context: isMobileOrTablet ? 'mobile_web_buy_bar' : 'desktop_web_buy_bar',
      });
    }
  };

  return (
    <OffersSection.Root>
      <BuyBarGlobalPricingInfo location="offer_buy_bar" />
      {isMobileOrTablet ? (
        <>
          <input
            type="text"
            ref={hiddenInput}
            style={{ position: 'absolute', margin: '-1000px -1000px' }}
          />
          <MobileOfferSectionListItem
            data-qa="mobile_offer_section_list_item"
            topOfferPrice={topOffer?.offerAmountCents?.amount}
            userOfferPrice={userOffer?.localizedOfferAmountCents?.amount}
            lastSalePrice={lastSoldPriceCents?.amount}
            isSneaker={isSneaker}
            handleOnClick={handleClick}
            remainingOfferDuration={userOffer && getUserRemainingOfferDuration(userOffer)}
            isLoading={isLoading}
          />
        </>
      ) : (
        <OffersSection.Table>
          <tbody>
            <OfferSectionListItem
              data-qa="offer_section_list_item"
              topOfferPrice={topOffer?.offerAmountCents?.amount}
              userOfferPrice={userOffer?.localizedOfferAmountCents?.amount}
              lastSalePrice={lastSoldPriceCents?.amount}
              isSneaker={isSneaker}
              handleOnClick={handleClick}
              isLoading={isLoading}
            />
          </tbody>
        </OffersSection.Table>
      )}
    </OffersSection.Root>
  );
};

OffersSection.Root = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.small - 1}px) {
    margin: 24px 16px;
  }
`;

OffersSection.Table = styled.table`
  width: 100%;
  margin-top: 32px;
`;
